import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Skeleton, 
  Toolbar, 
  Paper,
  Container,
  Card,
  CardContent,
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Description as DocumentIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Forward as ForwardIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

import { StyledCard, CardHeader } from './components/themeUtils';
import StatusStatsCard from './components/StatusStatsCard';
import RecentDocumentsList from './components/RecentDocumentsList';
import AssignedToMeList from './components/AssignedToMeList';
import AssignedByMeList from './components/AssignedByMeList';
import TaskDetailDialog from '../../components/tasks/TaskDetailDialogV2';
import { useDashboardData } from './components/useDashboardData';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RTLFormControl } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Modern styled components
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3, 3),
  marginBottom: theme.spacing(4),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '200px',
    height: '200px',
    background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
    borderRadius: '50%',
  }
}));

const StatsCard = styled(Card)(({ theme, color = 'primary' }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: `0 4px 20px ${alpha(theme.palette[color].main, 0.15)}`,
  border: `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.05)} 0%, ${theme.palette.background.paper} 100%)`,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 30px ${alpha(theme.palette[color].main, 0.25)}`,
  }
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  }
}));

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderRadius: theme.spacing(2),
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: theme.shadows[2],
}));

const Dashboard = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isRTL, getRTLProps } = useRTL();
  const [filter, setFilter] = useState('weekly');
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);

  // Fetch dashboard data
  const { isLoading: loading, isError: error, ...data } = useDashboardData(token, filter);

  // Set theme direction based on current language
  useEffect(() => {
    const direction = i18n.language === 'en' ? 'ltr' : 'rtl';
    document.body.dir = direction;
  }, [i18n.language]);

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleNavigateToAssignedByMe = () => {
    navigate('/tasks/assigned-by-me');
  };

  const handleNavigateToMyTasks = () => {
    navigate('/tasks');
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setTaskDetailOpen(true);
  };

  const handleTaskAcceptSuccess = (task) => {
    // Success toast is handled by useTaskOperations hook
    const isAssignee = task?.assignee_id === user?.id || task?.assignee?.id === user?.id;
    if (isAssignee) {
      navigate('/tasks?tab=in-progress');
    } else {
      navigate('/tasks/assigned-by-me?tab=in-progress');
    }
  };

  const handleNavigateToDocuments = () => {
    navigate('/documents');
  };

  const handleCreateDocument = () => {
    navigate('/documents/create');
  };

  const handleAssignTask = () => {
    navigate('/tasks/assigned-by-me?assign=1');
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2
        }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
          <Typography variant="h6" color="error" textAlign="center">
            {t('error_loading_dashboard', 'Error loading dashboard')}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {error}
          </Typography>
      </Box>
      </Container>
    );
  }

  // Show recent 10 actionable tasks ordered by urgency
  const actionableStatuses = ['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS'];

  const sortByUrgency = (tasksArr) => {
    return tasksArr
      .slice()
      .sort((a, b) => {
        const aOverdue = a.due_date && new Date(a.due_date) < new Date();
        const bOverdue = b.due_date && new Date(b.due_date) < new Date();
        if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

        const aDue = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bDue = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        if (aDue !== bDue) return aDue - bDue;

        const aAssigned = a.assigned_at ? new Date(a.assigned_at).getTime() : 0;
        const bAssigned = b.assigned_at ? new Date(b.assigned_at).getTime() : 0;
        return bAssigned - aAssigned;
      })
      .slice(0, 10);
  };

  const assignedByMeFiltered = Array.isArray(data.recentAssignedByMe)
    ? sortByUrgency(data.recentAssignedByMe.filter((task) => actionableStatuses.includes(task.status)))
    : [];

  const assignedToMeFiltered = Array.isArray(data.recentAssignedToMe)
    ? sortByUrgency(data.recentAssignedToMe.filter((task) => actionableStatuses.includes(task.status)))
    : [];

  // Calculate summary stats
  const totalAssignedByMe = Object.values(data.assignedByMeStats || {}).reduce((sum, count) => sum + count, 0);
  const totalMyAssignments = Object.values(data.myAssignmentsStats || {}).reduce((sum, count) => sum + count, 0);
  const totalDocuments = Array.isArray(data.recentDocuments) ? data.recentDocuments.length : 0;

  return (
    <Container maxWidth="xl" sx={{ py: 0, px: 0 }}>
      {/* Hero Section */}
      <HeroSection>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <Box sx={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
            <Typography 
              variant="h3" 
              fontWeight={700} 
              gutterBottom
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                direction: isRTL ? 'rtl' : 'ltr'
              }}
            >
              {t('welcome_back', 'Welcome back')}, {user?.username || user?.firstName || 'User'}!
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.9, 
                mb: 3,
                textAlign: isRTL ? 'right' : 'left',
                direction: isRTL ? 'rtl' : 'ltr'
              }}
            >
              {t('dashboard_subtitle', 'Here\'s what\'s happening with your documents today')}
            </Typography>
            
            {/* Quick Stats */}
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              flexWrap: 'wrap',
              justifyContent: isRTL ? 'flex-end' : 'flex-start'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon sx={{ fontSize: 20 }} />
                <Typography variant="body1" fontWeight={600}>
                  {totalAssignedByMe} {t('tasks_assigned', 'tasks assigned')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 20 }} />
                <Typography variant="body1" fontWeight={600}>
                  {totalMyAssignments} {t('my_tasks', 'my tasks')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DocumentIcon sx={{ fontSize: 20 }} />
                <Typography variant="body1" fontWeight={600}>
                  {totalDocuments} {t('documents', 'documents')}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <DashboardIcon sx={{ fontSize: 120, opacity: 0.1 }} />
          </Box>
        </Box>
      </HeroSection>

      {/* Filter Section */}
      <FilterContainer sx={{ mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isRTL ? 'row-reverse' : 'row',
          flexWrap: 'wrap',
          gap: 2
        }}>
            <RTLFormControl variant="outlined" size="small" sx={{ minWidth: 150 }} {...getRTLProps('select')}>
            <InputLabel>{t('time_period', 'Time Period')}</InputLabel>
            <Select 
              value={filter} 
              onChange={handleFilterChange} 
              label={t('time_period', 'Time Period')}
              startAdornment={<CalendarIcon sx={{ mr: isRTL ? 1 : 0, ml: isRTL ? 0 : 1, color: 'text.secondary' }} />}
            >
              <MenuItem value="daily">{t('daily', 'Daily')}</MenuItem>
                <MenuItem value="weekly">{t('weekly', 'Weekly')}</MenuItem>
              <MenuItem value="monthly">{t('monthly', 'Monthly')}</MenuItem>
              </Select>
            </RTLFormControl>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FilterIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              {t('dashboard_overview', 'Dashboard Overview')}
            </Typography>
          </Box>
        </Box>
      </FilterContainer>

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard onClick={handleCreateDocument}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ 
                width: 48, 
                height: 48, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'primary.main'
              }}>
                <AddIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('create_document', 'Create Document')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('create_new_document_desc', 'Add a new document to the system')}
              </Typography>
            </CardContent>
          </QuickActionCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard onClick={handleAssignTask}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ 
                width: 48, 
                height: 48, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'success.main'
              }}>
                <AssignmentIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('assign_task', 'Assign Task')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('assign_task_desc', 'Assign tasks to team members')}
              </Typography>
            </CardContent>
          </QuickActionCard>
        </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard onClick={handleNavigateToMyTasks}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Avatar sx={{ 
                      width: 48, 
                      height: 48, 
                      mx: 'auto', 
                      mb: 1.5,
                      bgcolor: 'warning.main'
                    }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('my_tasks', 'My Tasks')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('my_tasks_desc', 'View assigned tasks')}
                    </Typography>
                  </CardContent>
                </QuickActionCard>
              </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard onClick={handleNavigateToDocuments}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ 
                width: 48, 
                height: 48, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'info.main'
              }}>
                <DocumentIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('view_documents', 'View Documents')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('view_documents_desc', 'Browse all documents')}
              </Typography>
            </CardContent>
          </QuickActionCard>
        </Grid>
        
      </Grid>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
          <StatsCard color="primary" onClick={handleNavigateToAssignedByMe}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main" gutterBottom>
                    {totalAssignedByMe}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('assigned_by_me', 'Assigned By Me')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tasks_you_assigned', 'Tasks you have assigned to others')}
                  </Typography>
                </Box>
                <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                  <AssignmentIcon />
                </Avatar>
              </Box>
              <StatusStatsCard stats={data.assignedByMeStats || {}} statuses={['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED']} />
            </CardContent>
          </StatsCard>
          </Grid>
        
          <Grid item xs={12} md={6}>
          <StatsCard color="success" onClick={handleNavigateToMyTasks}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main" gutterBottom>
                    {totalMyAssignments}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('my_assignments', 'My Assignments')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tasks_assigned_to_you', 'Tasks assigned to you')}
                  </Typography>
                </Box>
                <Avatar sx={{ width: 48, height: 48, bgcolor: 'success.main' }}>
                  <PersonIcon />
                </Avatar>
              </Box>
              <StatusStatsCard stats={data.myAssignmentsStats || {}} statuses={['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED']} />
            </CardContent>
          </StatsCard>
        </Grid>
          </Grid>

      {/* Task Lists */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <StyledCard sx={{ cursor: 'pointer' }} onClick={handleNavigateToAssignedByMe}>
            <CardHeader title={t('assigned_by_me_tasks', 'Assigned By Me Tasks')} />
            <Box sx={{ p: 2 }}>
              <AssignedByMeList tasks={assignedByMeFiltered} onNavigate={handleTaskClick} />
            </Box>
            </StyledCard>
          </Grid>
        
        <Grid item xs={12} md={6}>
          <StyledCard sx={{ cursor: 'pointer' }} onClick={handleNavigateToMyTasks}>
            <CardHeader title={t('my_assigned_tasks', 'My Assigned Tasks')} />
            <Box sx={{ p: 2 }}>
              <AssignedToMeList tasks={assignedToMeFiltered} onNavigate={handleTaskClick} />
            </Box>
            </StyledCard>
        </Grid>
          </Grid>

          {/* Recent Documents */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
            <StyledCard sx={{ cursor: 'pointer' }} onClick={handleNavigateToDocuments}>
            <CardHeader title={t('recent_docs', 'Recent Documents')} />
            <Box sx={{ p: 2 }}>
              <RecentDocumentsList documents={Array.isArray(data.recentDocuments) ? data.recentDocuments : []} />
            </Box>
            </StyledCard>
          </Grid>
        </Grid>

      {/* Task Detail Dialog */}
        {taskDetailOpen && (
          <TaskDetailDialog
            task={selectedTask}
            open={taskDetailOpen}
            onClose={() => setTaskDetailOpen(false)}
            onSuccess={(task, action) => {
              switch(action) {
                case 'received': 
                  handleTaskAcceptSuccess(task);
                  break;
                case 'completed': 
                  handleTaskAcceptSuccess(task);
                  break;
                case 'rejected': 
                  break;
                case 'forwarded': 
                  break;
              }
            }}
          />
        )}
    </Container>
  );
};

export default Dashboard;